import React from "react";
import { Helmet } from "react-helmet";
import Topbar from "../components/Topbar";
import FootView from "./FootView";

interface LifeMedicineAcademyViewProps {
    pathName: string;
}

const LifeMedicineAcademyView = (props: LifeMedicineAcademyViewProps) => {
    return (
        <>
            <Helmet>
                <title>Life Medicine Academy - Välj ditt hälsopaket | Saluto</title>
                <meta
                    name="description"
                    content="Life Medicine Academy - Välj mellan START, LONGEVITY och LONGEVITY PLUS för omfattande hälsoanalyser"
                />
            </Helmet>

            <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pt-12 sm:pt-20 pb-8 px-4">
                <div className="w-4/5 mx-auto">
                    {/* Hero Section with Logos */}
                    <div className="text-center mb-12 animate-fadeIn">
                        {/* Introduction Text */}
                        <div className="text-center text-4xl font-bold">
                            <h1>Life Medicine Academy & Saluto</h1>
                        </div>
                        <div className="mb-8">
                            <p className="text-lg text-slate-700 max-w-3xl mx-auto leading-relaxed">
                                <a href="https://saluto.ai">SALUTO</a> är samarbetspartner till Life Medicine Academy och erbjuder plattformen där du kommer
                                kunna följa upp blodprover, kroppsmått och annan hälsodata. Provtagningen görs i samarbete
                                med <a href="https://www.maccpeople.se/">MACC People och Synlab</a></p>
                        </div>
                        {/* Logos Section */}
                        <div className="flex flex-row items-center justify-center gap-3 sm:gap-8 mb-10">
                            {/* Logo 1 */}
                            <div className="rounded-xl shadow-lg p-3 sm:p-6 w-28 h-20 sm:w-48 sm:h-32 flex items-center justify-center border border-slate-200">
                                <img
                                    src={require("../assets/images/logo.png").default}
                                    alt="Partner Logo"
                                    className="max-w-full max-h-full object-contain"
                                />
                            </div>

                            {/* Divider */}
                            <div className="text-xl sm:text-3xl text-indigo-600 font-bold">×</div>

                            {/* Logo 2 - Life Medicine Academy */}
                            <div className="rounded-xl shadow-lg w-28 h-20 sm:w-48 sm:h-32 flex items-center justify-center border border-slate-200">
                                <img
                                    src={require("../assets/images/lifemedicineacademy.png").default}
                                    alt="Life Medicine Academy Logo"
                                    className="max-w-full max-h-full object-contain"
                                />
                            </div>
                        </div>



                        <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-4 tracking-tight">
                            Fler levnadsår med god hälsa och energi
                        </h1>
                        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                            Hälsoplattformen Saluto kombinerar biomarkörer med AI-analys för att ge en tydlig, motiverande bild av hur din kropp mår – från energi och ämnesomsättning till återhämtning och biologisk ålder. Med hjälp av din smartphonekamera tas kroppsmåtten med en 3D-avbildning (SizeWall) för enkel, kontinuerlig uppföljning. Resultaten visualiseras i Saluto-appen och ger dig personliga insikter för långsiktigt god hälsa, balans och energi. 
                        </p>
                    </div>

                    {/* Package Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-4">

                        <div className="relative bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200 hover:shadow-2xl transition-all duration-300 group flex flex-col">
                            {/* Popular Badge */}


                            <div className="p-8 flex-grow flex flex-col">
                                {/* Header */}
                                <div className="mb-6">
                                    <h2 className="text-3xl font-bold text-slate-900 mb-2">START</h2>
                                    <p className="text-slate-600 text-sm">För dig som vill starta din hälsoresa och få kontroll över de viktigaste metabola markörerna.</p>
                                </div>

                                {/* Price */}
                                <div className="grid grid-cols-3 items-center gap-1 sm:gap-2">
                                    <div className="text-left">
                                        <div className="flex items-baseline">
                                            <span className="text-3xl sm:text-4xl font-extrabold">995</span>
                                            <span className="text-lg sm:text-xl text-slate-600">kr</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pb-8 pt-4">
                                    <p className="text-xs text-slate-500 italic">* Ingår ett blodprov</p>
                                </div>  

                                {/* Button */}
                                <button
                                    onClick={() => window.location.href = '/start-life-medicine-academy'}
                                    className="w-full py-4 px-6 rounded-xl font-semibold text-white mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                                    style={{ color: "white" }}
                                >
                                    Läs mer →
                                </button>

                                {/* Features */}
                                <div className="space-y-3 flex-grow">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Vad ingår</p>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">10 blodprovsmarkörer</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Blodsocker & blodfetter</span>
                                    </div>
                                     <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Insulin</span>
                                    </div>
                                </div>
                            </div>

                        </div>


                        {/* LONGEVITY Package */}
                        <div className="relative bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200 hover:shadow-2xl transition-all duration-300 group flex flex-col">

                            <div className="p-8 flex-grow flex flex-col">
                                {/* Header */}
                                <div className="mb-6">
                                    <h2 className="text-3xl font-bold text-slate-900 mb-2">LONGEVITY</h2>
                                    <p className="text-slate-600 text-sm">För dig som vill ta din hälsoresa vidare och mäta metabola markörer, vitaminer, mineraler och biologisk ålder.</p>
                                </div>

                                {/* Price */}
                                <div className="grid grid-cols-3 items-center gap-1 sm:gap-2">
                                    <div className="text-left">
                                        <div className="flex items-baseline">
                                            <span className="text-3xl sm:text-4xl font-extrabold">1995</span>
                                            <span className="text-lg sm:text-xl text-slate-600">kr</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pb-8 pt-4">
                                    <p className="text-xs text-slate-500 italic">* Ingår ett blodprov</p>
                                </div>  
                                {/* Button */}
                                <button
                                    onClick={() => window.location.href = '/longevity-life-medicine-academy'}
                                    className="w-full py-4 px-6 rounded-xl font-semibold text-white mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                                    style={{ color: "white" }}
                                >
                                    Läs mer →
                                </button>

                                {/* Features */}
                                <div className="space-y-3 flex-grow">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Vad ingår</p>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">45 blodprovsmarkörer</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Biologisk åldersberäkning</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Blodsocker & blodfetter utökad</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Lever & njurfunktion</span>
                                    </div>
                                     <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Insulin</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Sköldkörtel (TSH)</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Inflammation & blodstatus</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Näringsmarkörer (vitamin B9, B12)</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Järnlager (Ferritin)</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">3D-avatar för kroppsmätning (6 mån med SizeWall)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* LONGEVITY PLUS Package */}
                        <div className="relative bg-white rounded-3xl shadow-lg overflow-hidden border-2 border-indigo-200 hover:shadow-2xl transition-all duration-300 group flex flex-col">

                            <div className="p-8 flex-grow flex flex-col">
                                {/* Header */}
                                <div className="mb-6">
                                    <h2 className="text-3xl font-bold text-slate-900 mb-2">LONGEVITY PLUS</h2>
                                    <p className="text-slate-700 text-sm">För dig som är extra ambitiös och vill mäta både metabola markörer, vitaminer, mineraler, hormoner och biologisk ålder</p>
                                </div>

                                {/* Price */}
                                <div className="grid grid-cols-3 items-center gap-1 sm:gap-2">
                                    <div className="text-left">
                                        <div className="flex items-baseline">
                                            <span className="text-3xl sm:text-4xl font-extrabold">3195</span>
                                            <span className="text-lg sm:text-xl text-slate-600">kr</span>
                                        </div>
                                    </div>
                                </div>


                                <div className="pb-8 pt-4">
                                    <p className="text-xs text-slate-500 italic">* Ingår ett blodprov</p>
                                </div>  

                                {/* Button */}
                                <button
                                    onClick={() => window.location.href = '/longevityplus-life-medicine-academy'}
                                    className="w-full py-4 px-6 rounded-xl font-semibold text-white mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                                    style={{ color: "white" }}
                                >
                                    Läs mer →
                                </button>

                                {/* Features */}
                                <div className="space-y-3 flex-grow">
                                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-4">Vad ingår</p>
                                     <div className="flex items-start gap-3">
                                        <span className="text-blue-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">52 blodprovsmarkörer</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-indigo-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-800 text-sm font-medium">Allt i Longevity +</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-indigo-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Sköldkörtelmarkörer uttökad</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-indigo-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Stressmätning (Kortisol)</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-indigo-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Hormonbalans (Testosteron)</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-indigo-600 text-lg flex-shrink-0">✓</span>
                                        <span className="text-slate-700 text-sm">Näringsmarkörer (vitamin D, B9, B12)</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div className="flex justify-center ">  
                        <div className="text-center px-4 font-semibold text-sm">
                                Få 15% rabatt på uppföljande prover
                        </div>
                    </div>


                    {/* Additional Info */}
                    <div className="mt-16 text-center max-w-3xl mx-auto">
                        <h3 className="text-2xl font-bold text-slate-800 mb-4">
                            Varför välja Saluto?
                        </h3>
                        <p className="text-slate-600 leading-relaxed text-lg">
                            Våra hälsopaket kombinerar omfattande blodanalyser med AI-driven tolkning
                            för att ge dig djup insikt i din metabola hälsa. Få personliga rekommendationer
                            baserade på vetenskap och optimera din hälsa långsiktigt.
                        </p>
                    </div>
                </div>

                {/* Comparison Section */}
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8 my-12">
                    <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">
                        Jämför paketen
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-slate-200">
                                    <th className="text-left p-3 sm:p-4 text-slate-600"></th>
                                    <th className="text-center p-3 sm:p-4 text-blue-600 font-bold">START</th>
                                    <th className="text-center p-3 sm:p-4 text-blue-600 font-bold">LONGEVITY</th>
                                    <th className="text-center p-3 sm:p-4 text-indigo-600 font-bold">LONGEVITY PLUS</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-700 text-sm sm:text-base">
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Antal markörer</td>
                                    <td className="p-3 sm:p-4 text-center">10</td>
                                    <td className="p-3 sm:p-4 text-center">45</td>
                                    <td className="p-3 sm:p-4 text-center">52</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Biologisk ålder</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">x1</td>
                                    <td className="p-3 sm:p-4 text-center">x1</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">3D-avatar kroppsmätning</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">6 mån</td>
                                    <td className="p-3 sm:p-4 text-center">6 mån</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Blodsocker & blodfetter</td>
                                    <td className="p-3 sm:p-4 text-center">x8</td>
                                    <td className="p-3 sm:p-4 text-center">x11 (ink Apo-kvot)</td>
                                    <td className="p-3 sm:p-4 text-center">x12 (ink Lp(a))</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Insulin</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Lever & njurar</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">x11</td>
                                    <td className="p-3 sm:p-4 text-center">x11</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Inflammation & blodstatus</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">x15</td>
                                    <td className="p-3 sm:p-4 text-center">x15</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Sköldkörteln</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">TSH</td>
                                    <td className="p-3 sm:p-4 text-center">TSH, T3, T4, TPO</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Hormonbalans (kortisol & testosteron)</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Näringsmarkörer</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">B9, B12</td>
                                    <td className="p-3 sm:p-4 text-center">D, B9, B12</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                    <td className="p-3 sm:p-4">Järnlager (Ferritin)</td>
                                    <td className="p-3 sm:p-4 text-center">-</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                    <td className="p-3 sm:p-4 text-center">✓</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>



            <FootView pathName={props.pathName} />

            <style>{`
                @keyframes fadeIn {
                from {
                    opacity: 0;
                    transform: translateY(-20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
                }

                .animate-fadeIn {
                animation: fadeIn 0.8s ease-out;
                }
            `}</style>
        </>
    );
};

export default LifeMedicineAcademyView;
